import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
  AddressDto,
  CustomerDetailResponse,
  CustomerDuplicate,
  CustomerResponse,
  InvoiceResponse,
  OfferResponse,
  RequestResponse,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import {
  apiDelete,
  apiGet,
  apiGetPage,
  apiPatch,
  apiPost,
} from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import {
  DataList,
  FilterTabs,
  SearchBox,
  useUrlFilters,
} from "../../components/ui/controls.js";
import {
  ErrorPanel,
  ExportCsvLink,
  LoadingPanel,
  MutationError,
  formatDate,
  formatMoney,
} from "../shared.js";
import {
  DocumentPage,
  Notice,
  Panel,
  StatusTimeline,
} from "../commercial/document.js";
import { invoiceLabel } from "../invoices/invoice-common.js";
import {
  CustomerForm,
  createPayload,
  customerDraftValid,
  customerToDraft,
  emptyCustomer,
  updatePayload,
  type CustomerDraft,
} from "./CustomerForm.js";

function formatAddress(address: AddressDto) {
  return [address.line1, address.line2, `${address.postalCode} ${address.city}`]
    .filter(Boolean)
    .join(", ");
}

export function mapsUrl(address: AddressDto) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${address.line1}, ${address.postalCode} ${address.city}, ${address.country}`,
  )}`;
}

function CustomersListPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { values, page, set } = useUrlFilters(["status", "q"] as const);
  const status = values.status === "archived" ? "archived" : "active";
  const pageSize = 25;
  const query = useQuery({
    queryKey: ["customers", status, values.q, page],
    queryFn: () =>
      apiGetPage<CustomerResponse>("/customers", {
        status,
        q: values.q || undefined,
        page,
        pageSize,
      }),
    placeholderData: (previous) => previous,
  });
  return (
    <>
      <PageHeader
        title={t("customer.plural")}
        subtitle={t("customer.listSubtitle")}
        actions={
          <>
            <ExportCsvLink dataset="customers" query={{ q: values.q }} />
            {has("customers.create") ? (
              <Button as={Link} to="/customers/new" icon="pi pi-plus">
                {t("customer.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterTabs
          value={status}
          onChange={(next) => set({ status: next === "active" ? "" : next })}
          options={[
            { value: "active", label: t("customer.active") },
            { value: "archived", label: t("customer.archived") },
          ]}
        />
        <SearchBox
          value={values.q}
          onChange={(q) => set({ q })}
          placeholder={t("customer.searchPlaceholder")}
        />
      </div>
      {query.error ? (
        <ErrorPanel error={query.error} />
      ) : !query.data ? (
        <LoadingPanel />
      ) : (
        <DataList
          rows={query.data.data}
          rowKey={(customer) => customer.id}
          href={(customer) => `/customers/${customer.id}`}
          page={page}
          pageSize={pageSize}
          total={query.data.total}
          onPage={(next) => set({ page: next })}
          loading={query.isFetching || undefined}
          empty={values.q ? t("common.noMatches") : t("customer.empty")}
          columns={[
            {
              key: "name",
              header: t("common.name"),
              cell: (customer) => customer.displayName,
            },
            {
              key: "number",
              header: t("common.number"),
              cell: (customer) => (
                <span className="font-mono text-xs">{customer.number}</span>
              ),
              hideOnMobile: true,
            },
            {
              key: "address",
              header: t("common.address"),
              cell: (customer) =>
                customer.billingAddress
                  ? `${customer.billingAddress.postalCode} ${customer.billingAddress.city}`
                  : "—",
            },
            {
              key: "contact",
              header: t("customer.contact"),
              cell: (customer) => customer.phone ?? customer.email ?? "—",
            },
          ]}
        />
      )}
    </>
  );
}

function CustomerFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const existing = useQuery({
    queryKey: ["customer", id],
    queryFn: () =>
      apiGet<{ data: CustomerDetailResponse }>(`/customers/${id}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(id),
  });
  const [draft, setDraft] = useState<CustomerDraft>(emptyCustomer);
  useEffect(() => {
    if (existing.data) setDraft(customerToDraft(existing.data));
  }, [existing.data]);
  const save = useMutation({
    mutationFn: () =>
      id
        ? apiPatch<{ data: CustomerResponse }>(
            `/customers/${id}`,
            updatePayload(draft, existing.data!.version),
          )
        : apiPost<{ data: CustomerResponse; duplicates: CustomerDuplicate[] }>(
            "/customers",
            createPayload(draft),
          ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      void queryClient.invalidateQueries({
        queryKey: ["customer", response.data.id],
      });
      navigate(`/customers/${response.data.id}`);
    },
  });
  if (id && existing.isLoading) return <LoadingPanel />;
  if (existing.error) return <ErrorPanel error={existing.error} />;
  return (
    <form
      className="mx-auto max-w-3xl pb-24 md:pb-0"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader title={id ? t("customer.edit") : t("customer.new")} />
      <Panel>
        <CustomerForm value={draft} onChange={setDraft} excludeId={id} />
      </Panel>
      <MutationError error={save.error} />
      <div className="sticky bottom-16 z-10 mt-4 flex justify-end gap-2 rounded-xl border border-app-border bg-app-surface/95 px-4 py-3 backdrop-blur md:bottom-0">
        <Button
          type="button"
          variant="text"
          severity="secondary"
          onClick={() => navigate(-1)}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          loading={save.isPending || undefined}
          disabled={!customerDraftValid(draft)}
        >
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}

function CustomerRecords({ customer }: { customer: CustomerDetailResponse }) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const [tab, setTab] = useState<
    "requests" | "offers" | "invoices" | "projects"
  >("requests");
  const finance = has("finance.view");
  const tabs = [
    ...(has("requests.view_all") || has("requests.view_own")
      ? [
          {
            value: "requests" as const,
            label: `${t("request.plural")} (${customer.counts.requests})`,
          },
        ]
      : []),
    ...(finance && has("offers.view")
      ? [
          {
            value: "offers" as const,
            label: `${t("offer.plural")} (${customer.counts.offers})`,
          },
        ]
      : []),
    ...(finance && has("invoices.view")
      ? [
          {
            value: "invoices" as const,
            label: `${t("invoice.plural")} (${customer.counts.invoices})`,
          },
        ]
      : []),
    {
      value: "projects" as const,
      label: `${t("project.plural")} (${customer.counts.projects})`,
    },
  ];
  const active = tabs.some((entry) => entry.value === tab)
    ? tab
    : tabs[0]!.value;
  const requests = useQuery({
    queryKey: ["requests", "customer", customer.id],
    queryFn: () =>
      apiGetPage<RequestResponse>("/requests", {
        customerId: customer.id,
        pageSize: 50,
      }),
    enabled: active === "requests",
  });
  const offers = useQuery({
    queryKey: ["offers", "customer", customer.id],
    queryFn: () =>
      apiGetPage<OfferResponse>("/offers", {
        customerId: customer.id,
        pageSize: 50,
      }),
    enabled: active === "offers",
  });
  const invoices = useQuery({
    queryKey: ["invoices", "customer", customer.id],
    queryFn: () =>
      apiGetPage<InvoiceResponse>("/invoices", {
        customerId: customer.id,
        pageSize: 50,
      }),
    enabled: active === "invoices",
  });
  const rows: Array<{
    id: string;
    to: string;
    number: string;
    title: string;
    status: string;
    amount?: string;
  }> =
    active === "requests"
      ? (requests.data?.data ?? []).map((r) => ({
          id: r.id,
          to: `/requests/${r.id}`,
          number: r.number,
          title: r.shortDescription,
          status: r.status,
        }))
      : active === "offers"
        ? (offers.data?.data ?? []).map((o) => ({
            id: o.id,
            to: `/offers/${o.id}`,
            number: o.displayNumber,
            title: o.title,
            status: o.status,
            amount: formatMoney(o.grandTotal, o.currency),
          }))
        : active === "invoices"
          ? (invoices.data?.data ?? []).map((i) => ({
              id: i.id,
              to: `/invoices/${i.id}`,
              number: invoiceLabel(i, t),
              title: i.title ?? "",
              status:
                i.status === "draft" || i.status === "cancelled"
                  ? i.status
                  : i.paymentStatus,
              amount: formatMoney(i.grandTotal, i.currency),
            }))
          : customer.projects.map((p) => ({
              id: p.id,
              to: `/projects/${p.id}`,
              number: p.number,
              title: p.title,
              status: p.status,
            }));
  return (
    <Panel>
      <FilterTabs value={active} onChange={setTab} options={tabs} />
      <ul className="mt-3 divide-y divide-app-border">
        {rows.length === 0 ? (
          <li className="py-6 text-center text-sm text-app-muted">
            {t("common.none")}
          </li>
        ) : (
          rows.map((row) => (
            <li key={row.id}>
              <Link
                to={row.to}
                className="flex items-center justify-between gap-3 py-2.5 hover:bg-app-background"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {row.title || row.number}
                  </div>
                  <div className="font-mono text-xs text-app-muted">
                    {row.number}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {row.amount ? (
                    <span className="text-sm tabular-nums">{row.amount}</span>
                  ) : null}
                  <StatusBadge status={row.status} />
                </div>
              </Link>
            </li>
          ))
        )}
      </ul>
    </Panel>
  );
}

function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const customer = useQuery({
    queryKey: ["customer", id],
    queryFn: () =>
      apiGet<{ data: CustomerDetailResponse }>(`/customers/${id}`).then(
        (response) => response.data,
      ),
  });
  const archive = useMutation({
    mutationFn: (status: "active" | "archived") =>
      apiPatch<{ data: CustomerResponse }>(`/customers/${id}`, {
        version: customer.data!.version,
        status,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customer", id] });
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/customers/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      navigate("/customers");
    },
  });
  if (customer.isLoading) return <LoadingPanel />;
  if (customer.error || !customer.data)
    return <ErrorPanel error={customer.error} />;
  const data = customer.data;
  const archived = data.status === "archived";
  const person = [data.salutation, data.firstName, data.lastName]
    .filter(Boolean)
    .join(" ");
  return (
    <DocumentPage
      back={{ to: "/customers", label: t("customer.plural") }}
      number={data.number}
      status={archived ? "archived" : undefined}
      title={data.displayName}
      meta={data.type === "company" && person ? person : undefined}
      actions={
        <>
          {has("customers.edit") ? (
            <Button
              as={Link}
              to={`/customers/${data.id}/edit`}
              variant="outlined"
              severity="secondary"
              icon="pi pi-pencil"
            >
              {t("common.edit")}
            </Button>
          ) : null}
          {!archived && has("requests.create") ? (
            <Button
              as={Link}
              to={`/requests/new?customerId=${data.id}`}
              variant="outlined"
              icon="pi pi-inbox"
            >
              {t("request.new")}
            </Button>
          ) : null}
          {!archived && has("offers.create") && has("finance.view") ? (
            <Button
              as={Link}
              to={`/offers/new?customerId=${data.id}`}
              icon="pi pi-file"
            >
              {t("offer.new")}
            </Button>
          ) : null}
        </>
      }
      aside={
        <>
          <Panel title={t("customer.contact")}>
            <div className="space-y-2 text-sm">
              {data.phone ? (
                <a
                  href={`tel:${data.phone}`}
                  className="flex items-center gap-2 text-brand hover:underline"
                >
                  <i className="pi pi-phone" aria-hidden="true" /> {data.phone}
                </a>
              ) : null}
              {data.email ? (
                <a
                  href={`mailto:${data.email}`}
                  className="flex items-center gap-2 break-all text-brand hover:underline"
                >
                  <i className="pi pi-envelope" aria-hidden="true" />{" "}
                  {data.email}
                </a>
              ) : null}
              {data.preferredLocale ? (
                <div className="text-app-muted">
                  {t("customer.documentLanguage")}:{" "}
                  {t(`locale.${data.preferredLocale}`)}
                </div>
              ) : null}
            </div>
          </Panel>
          <Panel title={t("customer.billingAddress")}>
            {data.billingAddress ? (
              <a
                href={mapsUrl(data.billingAddress)}
                target="_blank"
                rel="noreferrer"
                className="text-sm hover:underline"
              >
                {formatAddress(data.billingAddress)}
              </a>
            ) : (
              <p className="text-sm text-app-muted">—</p>
            )}
          </Panel>
          <Panel title={t("customer.sites")}>
            {data.sites.length === 0 ? (
              <p className="text-sm text-app-muted">{t("customer.noSites")}</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.sites.map((site) => (
                  <li key={site.id}>
                    {site.label ? (
                      <div className="font-medium">{site.label}</div>
                    ) : null}
                    <a
                      href={mapsUrl(site)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-app-muted hover:underline"
                    >
                      {formatAddress(site)}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <StatusTimeline
            entityType="customer"
            id={data.id}
            version={data.version}
          />
          <p className="px-1 text-xs text-app-muted">
            {t("customer.since", { date: formatDate(data.createdAt) })}
          </p>
        </>
      }
    >
      {archived ? (
        <Notice tone="warning">{t("customer.archivedNotice")}</Notice>
      ) : null}
      {data.notes ? (
        <Panel title={t("common.notes")}>
          <p className="whitespace-pre-line text-sm">{data.notes}</p>
        </Panel>
      ) : null}
      <CustomerRecords customer={data} />
      <MutationError error={archive.error ?? remove.error} />
      {has("customers.edit") || has("customers.delete") ? (
        <div className="flex flex-wrap justify-end gap-2">
          {has("customers.edit") ? (
            <Button
              variant="text"
              severity="secondary"
              icon={archived ? "pi pi-replay" : "pi pi-box"}
              loading={archive.isPending || undefined}
              onClick={() => archive.mutate(archived ? "active" : "archived")}
            >
              {archived ? t("customer.restore") : t("customer.archive")}
            </Button>
          ) : null}
          {has("customers.delete") &&
          data.counts.requests +
            data.counts.offers +
            data.counts.invoices +
            data.counts.projects ===
            0 ? (
            <Button
              variant="text"
              severity="danger"
              icon="pi pi-trash"
              loading={remove.isPending || undefined}
              onClick={() =>
                window.confirm(t("customer.deleteConfirm")) && remove.mutate()
              }
            >
              {t("common.delete")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </DocumentPage>
  );
}

export const customersRoutes = [
  { path: "customers", element: <CustomersListPage /> },
  { path: "customers/new", element: <CustomerFormPage /> },
  { path: "customers/:id", element: <CustomerDetailPage /> },
  { path: "customers/:id/edit", element: <CustomerFormPage /> },
];
